import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * PhaseFormModal
 *
 * Props:
 * - open: boolean
 * - onClose()
 * - onSave(payload)
 * - phase: existing phase (optional, edit mode)
 * - project: project object (for date bounds)
 * - isPro: boolean (feature gate)
 */
export default function PhaseFormModal({
  open,
  onClose,
  onSave,
  phase = null,
  project,
  isPro = false,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  /* -------------------------------------------------------------------------- */
  /* 🔹 Init                                                                    */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    if (!open) return;

    setName(phase?.name || "");
    setDescription(phase?.description || "");
    setStartDate(phase?.startDate ? phase.startDate.slice(0, 10) : "");
    setEndDate(phase?.endDate ? phase.endDate.slice(0, 10) : "");
    setErr("");
  }, [open, phase]);

  if (!open) return null;

  /* -------------------------------------------------------------------------- */
  /* 🔹 Validation                                                              */
  /* -------------------------------------------------------------------------- */
  const validate = () => {
    if (!name.trim()) return "Phase name is required";

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      return "Start date must be before end date";
    }

    if (project?.startDate && startDate) {
      if (new Date(startDate) < new Date(project.startDate)) {
        return "Phase cannot start before project start date";
      }
    }

    if (project?.endDate && endDate) {
      if (new Date(endDate) > new Date(project.endDate)) {
        return "Phase cannot end after project end date";
      }
    }

    return null;
  };

  /* -------------------------------------------------------------------------- */
  /* 🔹 Save                                                                    */
  /* -------------------------------------------------------------------------- */
  const handleSave = async () => {
  

    const error = validate();
    if (error) {
      setErr(error);
      return;
    }

    setSaving(true);
    setErr("");

    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
          e.message ||
          "Failed to save phase"
      );
    } finally {
      setSaving(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /* 🔹 Render                                                                  */
  /* -------------------------------------------------------------------------- */
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {phase ? "Edit Phase" : "Add Phase"}
          </h3>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        {/* Encryption Notice */}

        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
          <p className="text-sm text-green-800">
            🔒 Information entered on this platform is end-to-end encrypted.
          </p>
        </div>
        
        {/* Name */}
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Phase name (e.g. Planning)"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />

        {/* Description */}
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Optional description"
          className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />

        {/* Dates */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        {/* Error */}
        {err && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">
            {err}
          </div>
        )}

        {/* Footer */}
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : phase ? "Update Phase" : "Add Phase"}
          </button>
        </div>
      </div>
    </div>
  );
}